import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Col,
  Flex,
  Paragraph,
  Row,
  Skeleton,
} from "@ir/ui";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import { PageContent } from "@src/presentation/components/Layout/common";
import { useParams } from "react-router";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BrandRepository } from "@src/infrastructure/repositories/brand";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { GetBrandDetailsUseCase } from "@src/application/useCases/brand";
import { useOrganizationsData } from "@src/presentation/providers/OrganizationsDataProvider";
import { useGlobalDataLookup } from "@src/presentation/providers/GlobalDataLookupProvider";
import { organizationKeysMatch } from "./utils/organizationDetailsResolve";
import { useOrganizationRouteFetch } from "./utils/organizationRouteFetch";
import {
  applyBrandFeatureStatusToBrand,
  countActiveBrandFeatures,
} from "./utils/brandFeatureUtils";
import {
  applyBrandPurchaserStatusToBrand,
  countActiveBrandPurchasers,
} from "./utils/brandPurchaserUtils";
import {
  Brand,
  BrandFeature,
  BrandPurchaser,
  BrandUser,
} from "@src/domain/entities/brand";
import {
  LoadingSpinner,
  type BreadcrumbItem,
} from "@src/presentation/components/common";
import {
  BrandDetailsFeaturesContent,
  BrandDetailsFeaturesHeader,
  BrandDetailsPageHeader,
  BrandDetailsPurchasersContent,
  BrandDetailsPurchasersHeader,
  BrandDetailsAccountingIntegrationContent,
  BrandDetailsAccountingIntegrationHeader,
  BrandDetailsStats,
  BrandDetailsUsersContent,
  BrandDetailsUsersHeader,
  useBrandDetailsAccountingIntegration,
} from "./utils";

const breadcrumbSkeleton = (
  <Skeleton height={16} width={160} animation="wave" variant="rounded" />
);

function getBrandPageSubtitle(
  organizationName: string,
  organizationKey: string | undefined,
  brandKey: string | undefined,
): string {
  if (organizationName) return `Brand in ${organizationName}`;
  if (organizationKey && brandKey) return "Loading brand details...";
  return "";
}

function buildBrandDetailsBreadcrumbItems({
  organizationKey,
  organizationName,
  stillLoadingOrg,
  displayName,
  brandLoading,
}: {
  organizationKey: string | undefined;
  organizationName: string;
  stillLoadingOrg: boolean;
  displayName: string;
  brandLoading: boolean;
}): BreadcrumbItem[] {
  const root: BreadcrumbItem = {
    key: "organizations-root",
    title: "Organizations & Brands",
    path: "/organizations",
  };
  const brandTitle =
    displayName ||
    (brandLoading || stillLoadingOrg ? breadcrumbSkeleton : "Brand");

  if (!organizationKey) {
    return [
      root,
      {
        key: "brand-current",
        title: displayName || (brandLoading ? breadcrumbSkeleton : "Brand"),
      },
    ];
  }

  return [
    root,
    {
      key: `organization-${organizationKey}`,
      title:
        organizationName ||
        (stillLoadingOrg ? breadcrumbSkeleton : organizationKey),
      path: `/organizations/${organizationKey}`,
    },
    {
      key: "brand-current",
      title: brandTitle,
    },
  ];
}

export const BrandDetails = () => {
  const { organizationKey, brandKey } = useParams<{
    organizationKey: string;
    brandKey: string;
  }>();
  useOrganizationRouteFetch(organizationKey);

  const {
    organizationDetails,
    organizationDetailsKey,
    organizationDetailsLoading,
    getCachedBrandDetails,
    setCachedBrandDetails,
    applyBrandUpdate,
    applyBrandDetailsSave,
  } = useOrganizationsData();

  const [brand, setBrand] = useState<Brand | null>(null);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandError, setBrandError] = useState<string | null>(null);
  const [accountingConnected, setAccountingConnected] = useState(false);
  const [accountingStatusLoading, setAccountingStatusLoading] = useState(false);
  const [accountingSystemName, setAccountingSystemName] = useState<string | null>(
    null,
  );

  const {
    featuresLookup,
    accountingSystemsLookup,
    accountingSystemsLookupLoading,
    accountingSystemsLookupError,
  } = useGlobalDataLookup();

  const orgDetailsForThisOrg =
    organizationKey != null &&
    organizationDetails != null &&
    (organizationDetailsKey === organizationKey ||
      organizationKeysMatch(
        organizationDetails.organizationKey,
        organizationKey,
      ));
  const brandId = orgDetailsForThisOrg
    ? (organizationDetails.brands?.find((b) => b.brandKey === brandKey)
        ?.brandId ?? null)
    : null;

  useEffect(() => {
    if (brandId == null) return;

    const cachedBrand = getCachedBrandDetails(brandId);
    if (cachedBrand) {
      setBrand(cachedBrand);
      setBrandLoading(false);
      setBrandError(null);
      return;
    }

    let ignore = false;
    setBrandLoading(true);
    setBrandError(null);
    const repo = new BrandRepository(GraphQlAdapter.getInstance().getClient());
    new GetBrandDetailsUseCase(repo)
      .execute(brandId)
      .then(({ brand: b }) => {
        if (!ignore) {
          setCachedBrandDetails(brandId, b);
          setBrand(b);
          setBrandLoading(false);
        }
      })
      .catch((err) => {
        if (!ignore) {
          setBrandError(
            err instanceof Error ? err.message : "Failed to load brand",
          );
          setBrand(null);
          setBrandLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [brandId, getCachedBrandDetails, setCachedBrandDetails]);

  const displayName = brand
    ? (brand.brandDisplayName ?? brand.brandName ?? "")
    : "";
  const brandDisplayKey = brand?.brandKey ?? brandKey ?? "";
  const accountingIntegration = useBrandDetailsAccountingIntegration({
    brandKey: brandDisplayKey,
    items: accountingSystemsLookup,
    onConnectionStatusChange: setAccountingConnected,
    onStatusLoadingChange: setAccountingStatusLoading,
    onSelectedSystemNameChange: setAccountingSystemName,
  });
  const brandDescription = brand?.brandDescription ?? null;
  const organizationName =
    orgDetailsForThisOrg && organizationDetails
      ? (organizationDetails.organizationDisplayName ??
        organizationDetails.organizationName ??
        "")
      : "";
  const brandFeatures = brand?.brandFeatures ?? null;
  const brandPurchasers = brand?.brandPurchasers ?? null;
  const brandUsers = brand?.brandUsers ?? null;
  const stillLoadingOrg = Boolean(
    organizationKey && (organizationDetailsLoading || !orgDetailsForThisOrg),
  );
  const showInvalid =
    !organizationKey ||
    !brandKey ||
    (orgDetailsForThisOrg && brandId == null && !brandLoading);

  const showLoading =
    (brandLoading || !brand || stillLoadingOrg) && !brandError && !showInvalid;

  const activeUsersCount = useMemo(() => {
    const users = brandUsers ?? [];
    return users.filter((user) => user.brandUserIsActive).length;
  }, [brandUsers]);

  const activeFeaturesCount = useMemo(
    () => countActiveBrandFeatures(brandFeatures),
    [brandFeatures],
  );

  const activePurchasersCount = useMemo(
    () => countActiveBrandPurchasers(brandPurchasers),
    [brandPurchasers],
  );

  const handleFeatureStatusUpdated = useCallback(
    (updatedFeature: BrandFeature, featureKey: string) => {
      setBrand((prev) => {
        if (!prev) return prev;
        const nextBrand = applyBrandFeatureStatusToBrand(
          prev,
          updatedFeature,
          featureKey,
        );
        setCachedBrandDetails(nextBrand.brandId, nextBrand);
        applyBrandUpdate(nextBrand);
        return nextBrand;
      });
    },
    [applyBrandUpdate, setCachedBrandDetails],
  );

  const handleUserStatusUpdated = useCallback(
    (updatedUser: BrandUser) => {
      setBrand((prev) => {
        if (!prev) return prev;
        const users = prev.brandUsers ?? [];
        const index = users.findIndex(
          (u) => u.brandUserId === updatedUser.brandUserId,
        );
        const nextUsers =
          index >= 0
            ? users.map((u, i) => (i === index ? updatedUser : u))
            : [...users, updatedUser];
        const nextBrand = Brand.withProps(prev, { brandUsers: nextUsers });
        setCachedBrandDetails(nextBrand.brandId, nextBrand);
        return nextBrand;
      });
    },
    [setCachedBrandDetails],
  );

  const handlePurchaserStatusUpdated = useCallback(
    (updatedPurchaser: BrandPurchaser) => {
      setBrand((prev) => {
        if (!prev) return prev;
        const nextBrand = applyBrandPurchaserStatusToBrand(prev, updatedPurchaser);
        setCachedBrandDetails(nextBrand.brandId, nextBrand);
        applyBrandUpdate(nextBrand);
        return nextBrand;
      });
    },
    [applyBrandUpdate, setCachedBrandDetails],
  );

  const pageSubtitle = getBrandPageSubtitle(
    organizationName,
    organizationKey,
    brandKey,
  );

  const breadcrumbItems = useMemo(
    () =>
      buildBrandDetailsBreadcrumbItems({
        organizationKey,
        organizationName,
        stillLoadingOrg,
        displayName,
        brandLoading,
      }),
    [
      organizationKey,
      organizationName,
      stillLoadingOrg,
      displayName,
      brandLoading,
    ],
  );

  let mainContent: ReactNode = null;
  if (showLoading) {
    mainContent = <LoadingSpinner />;
  } else if (brand) {
    mainContent = (
      <>
        <BrandDetailsStats
          brandName={displayName}
          brandKey={brandDisplayKey}
        />

        {brandDescription && (
          <Card className="bg-white mb-4" variant="outlined" shadow>
            <CardContent>
              <Paragraph className="text-grey-500 font-regular m-0 mb-1">
                Description
              </Paragraph>
              <Paragraph className="text-grey-700 m-0">
                {brandDescription}
              </Paragraph>
            </CardContent>
          </Card>
        )}

        <Row>
        <Col span={12} md={6} className="mb-3">
          <Card className="bg-white" variant="outlined" shadow>
            <CardHeader className="border-bottom-1 border-color-grey-50 pb-2">
              <BrandDetailsFeaturesHeader
                activeFeaturesCount={activeFeaturesCount}
              />
            </CardHeader>
            <CardContent className="brand-card">
              <BrandDetailsFeaturesContent
                brandId={brandId as number}
                featuresLookup={featuresLookup}
                brandFeatures={brandFeatures}
                onFeatureStatusUpdated={handleFeatureStatusUpdated}
              />
            </CardContent>
          </Card>
        </Col>
        <Col span={12} md={6}>
          <Card className="bg-white" variant="outlined" shadow>
            <CardHeader className="border-bottom-1 border-color-grey-50 pb-2">
              <BrandDetailsUsersHeader activeUsersCount={activeUsersCount} />
            </CardHeader>
            <CardContent className="brand-card">
              <BrandDetailsUsersContent
                brandId={brandId as number}
                brandUsers={brandUsers}
                onUserStatusUpdated={handleUserStatusUpdated}
              />
            </CardContent>
          </Card>
        </Col>
        <Col span={12} md={6}>
          <Card className="bg-white" variant="outlined" shadow>
            <CardHeader className="border-bottom-1 border-color-grey-50 pb-2">
              <BrandDetailsPurchasersHeader activePurchasersCount={activePurchasersCount} />
            </CardHeader>
            <CardContent className="brand-card">
              <BrandDetailsPurchasersContent
                brandId={brandId as number}
                brandPurchasers={brandPurchasers}
                onPurchaserStatusUpdated={handlePurchaserStatusUpdated}
              />
            </CardContent>
          </Card>
        </Col>
        <Col span={12} md={6} className="mb-3">
          <Card className="bg-white" variant="outlined" shadow>
            <CardHeader className="border-bottom-1 border-color-grey-50 pb-2">
              <BrandDetailsAccountingIntegrationHeader
                connected={accountingConnected}
                statusLoading={accountingStatusLoading}
                systemName={accountingSystemName}
              />
            </CardHeader>
            <CardContent className="brand-card">
              <BrandDetailsAccountingIntegrationContent
                loading={accountingSystemsLookupLoading}
                error={accountingSystemsLookupError}
                connecting={accountingIntegration.connecting}
                disconnecting={accountingIntegration.disconnecting}
                checkingStatus={accountingIntegration.checkingStatus}
                selectedKey={accountingIntegration.selectedKey}
                options={accountingIntegration.options}
                selectedOption={accountingIntegration.selectedOption}
                onSelectedKeyChange={accountingIntegration.onSelectedKeyChange}
                pendingKey={accountingIntegration.pendingKey}
                pendingOption={accountingIntegration.pendingOption}
                switching={accountingIntegration.switching}
                onConfirmSwitch={accountingIntegration.confirmSwitch}
                onCancelSwitch={accountingIntegration.cancelSwitch}
              />
            </CardContent>
            <CardFooter>
              <Flex
                justifyContent="space-between"
                gap={0.5}
                style={{ width: "100%" }}
              >
                <Flex>
                  {accountingConnected && (
                    <Button
                      isDisabled={accountingIntegration.actionsDisabled}
                      onPress={accountingIntegration.handleForceDisconnect}
                      color="error"
                    >
                      {accountingIntegration.disconnecting
                        ? "Disconnecting..."
                        : "Disconnect"}
                    </Button>
                  )}
                </Flex>
                <Flex>
                  <Button
                    isDisabled={accountingIntegration.actionsDisabled}
                    onPress={
                      accountingIntegration.handleCheckConnectionStatus
                    }
                    color="grey"
                    variant="outline"
                  >
                    {accountingIntegration.checkingStatus
                      ? "Checking..."
                      : "Check Connection Status"}
                  </Button>
                  <Button
                    isDisabled={accountingIntegration.actionsDisabled}
                    onPress={accountingIntegration.handleConnect}
                  >
                    {accountingIntegration.connectButtonLabel}
                  </Button>
                </Flex>
              </Flex>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      </>
    );
  }

  const orgId = organizationDetails?.organizationId ?? 0;
  const orgKey = organizationDetails?.organizationKey ?? organizationKey ?? "";

  return (
    <ErrorBoundary>
      <BrandDetailsPageHeader
        brand={brand}
        organizationId={orgId}
        organizationKey={orgKey}
        headerTitle={displayName}
        subtitle={pageSubtitle}
        brandLoading={brandLoading || stillLoadingOrg}
        breadcrumbItems={breadcrumbItems}
        onBrandSaved={(saved) => {
          setCachedBrandDetails(saved.brandId, saved);
          setBrand(saved);
          if (organizationKey) {
            applyBrandDetailsSave(saved, organizationKey);
          } else {
            applyBrandDetailsSave(saved);
          }
        }}
      />
      <PageContent className="p-3">
        {brandError && !brand && (
          <Paragraph className="text-danger m-0">{brandError}</Paragraph>
        )}
        {showInvalid &&
          !brandLoading &&
          !organizationDetailsLoading &&
          organizationKey &&
          brandKey && (
            <Paragraph className="text-grey-500 m-0">Invalid brand.</Paragraph>
          )}
        {mainContent}
      </PageContent>
    </ErrorBoundary>
  );
};
